import { DateRange } from '@mui/x-date-pickers-pro';
import { useMemo } from 'react';
import { useParams } from 'react-router';

import { ModuleEntityType } from '~/common/types';
import { DateRangeProvider } from '~/components/DateRangeProvider';
import { DEFAULT_DATE_RANGE } from '~/components/DateRangeProvider/DateRangeProvider.util';
import { ModuleContextProvider, ModuleContextProviderValue } from '~/components/ModuleContextProvider';
import { ModulePage } from '~/components/ModulePage/ModulePage';
import { useModulePageConfig } from '~/components/ModulePageConfigProvider';
import { ModuleSlot } from '~/components/ModuleSlot/ModuleSlot';
import { ResponsibleUsersProvider } from '~/components/ResponsibleUsersProvider';
import { ModuleSlotId } from '~/modules';
import { RouteId } from '~/routes';

import { SupplierDetailHeader } from './SupplierDetailHeader/SupplierDetailHeader';

const SUPPLIER_TAB_INITIAL_DATE_RANGE: Record<string, DateRange<Date>> = {
  'autonomous-procurement': [null, null],
};

const getInitialDateRangeForSupplierTab = (tabId?: string): DateRange<Date> => {
  return (tabId && SUPPLIER_TAB_INITIAL_DATE_RANGE[tabId]) || DEFAULT_DATE_RANGE;
};

type InnerSupplierDetailProps = {
  supplierId: string;
  tabId?: string;
};

const InnerSupplierDetail = ({ supplierId, tabId }: InnerSupplierDetailProps) => {
  const { modules, tabs } = useModulePageConfig({
    pageId: RouteId.supplierDetail(),
    tabId,
  });

  const contextValue = useMemo<ModuleContextProviderValue>(
    () => ({ entity: ModuleEntityType.SUPPLIER, entityId: supplierId }),
    [supplierId],
  );

  return (
    <ResponsibleUsersProvider>
      <ModulePage
        header={<SupplierDetailHeader id={supplierId} tabs={tabs} />}
        modules={
          <ModuleContextProvider value={contextValue}>
            <ModuleSlot id={ModuleSlotId.MAIN} modules={modules} />
          </ModuleContextProvider>
        }
      />
    </ResponsibleUsersProvider>
  );
};

export const SupplierDetail = () => {
  const { supplierId, tabId } = useParams() as { supplierId: string; tabId?: string };
  const initialDateRangeDefault = getInitialDateRangeForSupplierTab(tabId);

  return (
    <DateRangeProvider key={`${supplierId}:${tabId ?? 'default'}`} initialDateRangeDefault={initialDateRangeDefault}>
      <InnerSupplierDetail supplierId={supplierId} tabId={tabId} />
    </DateRangeProvider>
  );
};
